import React, { useState, useEffect, useRef } from 'react';
import { branding } from '../../constants/branding';
import { store } from '../../store';

interface SimpleStrategyConfigProps {
  botId: number;
  botName?: string;
  botStrategy?: string;
}

const SimpleStrategyConfig: React.FC<SimpleStrategyConfigProps> = ({ botId, botName, botStrategy }) => {
  const [strategy, setStrategy] = useState('MA_CROSS');
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [parameters, setParameters] = useState({
    // MA_CROSS parameters
    short_window: 10,
    long_window: 30,
    position_size_pct: 10,
    stop_loss_pct: 2.0,
    take_profit_pct: 5.0,
    // RSI parameters
    rsi_period: 14,
    oversold: 30,
    overbought: 70,
    // BOLLINGER parameters
    period: 20,
    std_dev: 2.0,
    // MACD parameters
    fast_period: 12,
    slow_period: 26,
    signal_period: 9,
    threshold: 0.0,
    // STOCHASTIC parameters
    k_period: 14,
    d_period: 3,
    overbought_level: 80,
    oversold_level: 20,
    // ICHIMOKU parameters
    conversion_period: 9,
    base_period: 26,
    leading_span_b_period: 52,
    displacement: 26,
    // PARABOLIC_SAR parameters
    acceleration_factor: 0.02,
    maximum_acceleration: 0.2,
    // VWAP parameters
    vwap_period: 20,
    deviation_threshold: 0.02,
    volume_threshold: 1.5,
    // ATR parameters
    atr_period: 14,
    atr_multiplier: 2.0,
    volatility_threshold: 0.02,
    // HYBRID parameters
    primary_strategy: 'MA_CROSS',
    secondary_strategy: 'RSI',
    confirmation_threshold: 0.5,
    weight_primary: 0.7,
    weight_secondary: 0.3
  });

  // Set the bot's actual strategy from props
  useEffect(() => {
    console.log('SimpleStrategyConfig: botStrategy prop received:', botStrategy);
    if (botStrategy) {
      setStrategy(botStrategy);
      setInitialized(true);
      console.log('SimpleStrategyConfig: strategy set to:', botStrategy);
    }
  }, [botStrategy]);

  // Also try to fetch any existing strategy configuration parameters from Java service
  const configFetchedRef = useRef(false);
  useEffect(() => {
    const fetchBotStrategyParams = async () => {
      // Prevent multiple fetches for the same botId
      if (configFetchedRef.current || !botId || !botStrategy) {
        return;
      }
      
      try {
        setLoading(true);
        configFetchedRef.current = true;
        // Try to fetch the bot's current strategy configuration parameters
        const { getApiBaseUrl } = await import('../../utils/apiConfig');
        const apiBaseUrl = getApiBaseUrl();
        const state = store.getState();
        const token = state.auth.token;
        const authType = state.auth.type;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token && authType === 'JWT') headers['Authorization'] = `Bearer ${token}`;
        
        const response = await fetch(`${apiBaseUrl}/api/trading/bots/${botId}/strategy/config`, {
          credentials: 'include',
          headers
        });
        if (response.ok) {
          const config = await response.json();
          // Handle both direct config object and wrapped response
          const configData = config.data || config;
          if (configData.parameters) {
            setParameters(prev => ({ ...prev, ...configData.parameters }));
          }
        } else if (response.status !== 404) {
          // 404 is expected if no config exists yet, other errors should be logged
          console.warn('Failed to fetch strategy config:', response.status, response.statusText);
        }
      } catch (error) {
        console.log('No existing strategy parameters found, using defaults');
      } finally {
        setLoading(false);
      }
    };

    if (botId && botStrategy) {
      fetchBotStrategyParams();
    }
    
    // Reset ref when botId changes
    return () => {
      configFetchedRef.current = false;
    };
  }, [botId, botStrategy]);

  const handleSave = async () => {
    // Filter parameters to only include relevant ones for the selected strategy
    const getRelevantParameters = (strategyName: string) => {
      const allParams = parameters;
      
      switch (strategyName) {
        case 'MA_CROSS':
          return {
            short_window: allParams.short_window,
            long_window: allParams.long_window,
            position_size_pct: allParams.position_size_pct,
            stop_loss_pct: allParams.stop_loss_pct,
            take_profit_pct: allParams.take_profit_pct
          };
        case 'RSI':
          return {
            rsi_period: allParams.rsi_period,
            oversold: allParams.oversold,
            overbought: allParams.overbought,
            position_size_pct: allParams.position_size_pct
          };
        case 'BOLLINGER':
          return {
            period: allParams.period,
            std_dev: allParams.std_dev,
            position_size_pct: allParams.position_size_pct
          };
        case 'MACD':
          return {
            fast_period: allParams.fast_period,
            slow_period: allParams.slow_period,
            signal_period: allParams.signal_period,
            threshold: allParams.threshold
          };
        case 'STOCHASTIC':
          return {
            k_period: allParams.k_period,
            d_period: allParams.d_period,
            overbought_level: allParams.overbought_level,
            oversold_level: allParams.oversold_level
          };
        case 'ICHIMOKU':
          return {
            conversion_period: allParams.conversion_period,
            base_period: allParams.base_period,
            leading_span_b_period: allParams.leading_span_b_period,
            displacement: allParams.displacement
          };
        case 'PARABOLIC_SAR':
          return {
            acceleration_factor: allParams.acceleration_factor,
            maximum_acceleration: allParams.maximum_acceleration,
            threshold: allParams.threshold
          };
        case 'VWAP':
          return {
            vwap_period: allParams.vwap_period,
            deviation_threshold: allParams.deviation_threshold,
            volume_threshold: allParams.volume_threshold
          };
        case 'ATR':
          return {
            atr_period: allParams.atr_period,
            atr_multiplier: allParams.atr_multiplier,
            volatility_threshold: allParams.volatility_threshold
          };
        case 'HYBRID':
          return {
            primary_strategy: allParams.primary_strategy,
            secondary_strategy: allParams.secondary_strategy,
            confirmation_threshold: allParams.confirmation_threshold,
            weight_primary: allParams.weight_primary,
            weight_secondary: allParams.weight_secondary
          };
        default:
          return {};
      }
    };

    const config = {
      strategy_name: strategy,
      parameters: getRelevantParameters(strategy)
    };
    
    console.log('Saving strategy config:', config);
    console.log('Strategy:', strategy);
    console.log('Filtered parameters:', getRelevantParameters(strategy));
    setLoading(true);
    
    try {
      // Call Java service to save strategy configuration
      const { getApiBaseUrl } = await import('../../utils/apiConfig');
      const apiBaseUrl = getApiBaseUrl();
      const state = store.getState();
      const token = state.auth.token;
      const authType = state.auth.type;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && authType === 'JWT') headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(`${apiBaseUrl}/api/trading/bots/${botId}/strategy/config`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(config)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Strategy config saved successfully:', result);
        alert('Strategy configuration saved successfully!');
      } else {
        const error = await response.json();
        console.error('Failed to save strategy config:', error);
        alert('Failed to save strategy configuration: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving strategy config:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert('Error saving strategy configuration: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    return (
      <div style={{ background: '#fff', border: `1px solid ${branding.borderColor}`, borderRadius: 8, padding: 20 }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Loading strategy configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', border: `1px solid ${branding.borderColor}`, borderRadius: 8, padding: 20 }}>
      <h3 style={{ marginTop: 0, marginBottom: 20 }}>
        Strategy Configuration for {botName || `Bot ${botId}`}
      </h3>
      
      <div className="form-group" style={{ marginBottom: 20 }}>
        <label>Strategy Template</label>
        <select
          value={strategy}
          onChange={(e) => setStrategy(e.target.value)}
          className="form-control"
          disabled={loading}
        >
          <option value="MA_CROSS">Moving Average Crossover</option>
          <option value="RSI">RSI Mean Reversion</option>
          <option value="BOLLINGER">Bollinger Bands</option>
          <option value="MACD">MACD (Moving Average Convergence Divergence)</option>
          <option value="STOCHASTIC">Stochastic Oscillator</option>
          <option value="ICHIMOKU">Ichimoku Cloud</option>
          <option value="PARABOLIC_SAR">Parabolic SAR</option>
          <option value="VWAP">VWAP (Volume Weighted Average Price)</option>
          <option value="ATR">ATR (Average True Range)</option>
          <option value="HYBRID">Hybrid Strategy</option>
        </select>
      </div>

      {strategy === 'MA_CROSS' && (
        <div style={{ marginBottom: 20 }}>
          <h4>MA_CROSS Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>Short Window</label>
              <input
                type="number"
                min="5"
                max="50"
                value={parameters.short_window}
                onChange={(e) => setParameters({...parameters, short_window: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Long Window</label>
              <input
                type="number"
                min="20"
                max="200"
                value={parameters.long_window}
                onChange={(e) => setParameters({...parameters, long_window: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Position Size %</label>
              <input
                type="number"
                min="1"
                max="20"
                step="0.1"
                value={parameters.position_size_pct}
                onChange={(e) => setParameters({...parameters, position_size_pct: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Stop Loss %</label>
              <input
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={parameters.stop_loss_pct}
                onChange={(e) => setParameters({...parameters, stop_loss_pct: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Take Profit %</label>
              <input
                type="number"
                min="0.1"
                max="20"
                step="0.1"
                value={parameters.take_profit_pct}
                onChange={(e) => setParameters({...parameters, take_profit_pct: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'RSI' && (
        <div style={{ marginBottom: 20 }}>
          <h4>RSI Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>RSI Period</label>
              <input
                type="number"
                min="5"
                max="50"
                value={parameters.rsi_period}
                onChange={(e) => setParameters({...parameters, rsi_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Oversold Threshold</label>
              <input
                type="number"
                min="10"
                max="40"
                step="1"
                value={parameters.oversold}
                onChange={(e) => setParameters({...parameters, oversold: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Overbought Threshold</label>
              <input
                type="number"
                min="60"
                max="90"
                step="1"
                value={parameters.overbought}
                onChange={(e) => setParameters({...parameters, overbought: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Position Size %</label>
              <input
                type="number"
                min="1"
                max="20"
                step="0.1"
                value={parameters.position_size_pct}
                onChange={(e) => setParameters({...parameters, position_size_pct: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'BOLLINGER' && (
        <div style={{ marginBottom: 20 }}>
          <h4>Bollinger Bands Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>Period</label>
              <input
                type="number"
                min="10"
                max="50"
                value={parameters.period}
                onChange={(e) => setParameters({...parameters, period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Standard Deviation</label>
              <input
                type="number"
                min="1.0"
                max="3.0"
                step="0.1"
                value={parameters.std_dev}
                onChange={(e) => setParameters({...parameters, std_dev: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Position Size %</label>
              <input
                type="number"
                min="1"
                max="20"
                step="0.1"
                value={parameters.position_size_pct}
                onChange={(e) => setParameters({...parameters, position_size_pct: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'MACD' && (
        <div style={{ marginBottom: 20 }}>
          <h4>MACD Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>Fast Period</label>
              <input
                type="number"
                min="5"
                max="50"
                value={parameters.fast_period || 12}
                onChange={(e) => setParameters({...parameters, fast_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Slow Period</label>
              <input
                type="number"
                min="20"
                max="100"
                value={parameters.slow_period || 26}
                onChange={(e) => setParameters({...parameters, slow_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Signal Period</label>
              <input
                type="number"
                min="3"
                max="20"
                value={parameters.signal_period || 9}
                onChange={(e) => setParameters({...parameters, signal_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'STOCHASTIC' && (
        <div style={{ marginBottom: 20 }}>
          <h4>Stochastic Oscillator Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>%K Period</label>
              <input
                type="number"
                min="5"
                max="50"
                value={parameters.k_period || 14}
                onChange={(e) => setParameters({...parameters, k_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>%D Period</label>
              <input
                type="number"
                min="2"
                max="10"
                value={parameters.d_period || 3}
                onChange={(e) => setParameters({...parameters, d_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Overbought Level</label>
              <input
                type="number"
                min="70"
                max="95"
                value={parameters.overbought_level || 80}
                onChange={(e) => setParameters({...parameters, overbought_level: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Oversold Level</label>
              <input
                type="number"
                min="5"
                max="30"
                value={parameters.oversold_level || 20}
                onChange={(e) => setParameters({...parameters, oversold_level: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'ICHIMOKU' && (
        <div style={{ marginBottom: 20 }}>
          <h4>Ichimoku Cloud Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>Conversion Period</label>
              <input
                type="number"
                min="5"
                max="20"
                value={parameters.conversion_period || 9}
                onChange={(e) => setParameters({...parameters, conversion_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Base Period</label>
              <input
                type="number"
                min="20"
                max="50"
                value={parameters.base_period || 26}
                onChange={(e) => setParameters({...parameters, base_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Leading Span B Period</label>
              <input
                type="number"
                min="40"
                max="100"
                value={parameters.leading_span_b_period || 52}
                onChange={(e) => setParameters({...parameters, leading_span_b_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Displacement</label>
              <input
                type="number"
                min="20"
                max="50"
                value={parameters.displacement || 26}
                onChange={(e) => setParameters({...parameters, displacement: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'PARABOLIC_SAR' && (
        <div style={{ marginBottom: 20 }}>
          <h4>Parabolic SAR Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>Acceleration Factor</label>
              <input
                type="number"
                min="0.01"
                max="0.1"
                step="0.01"
                value={parameters.acceleration_factor || 0.02}
                onChange={(e) => setParameters({...parameters, acceleration_factor: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Maximum Acceleration</label>
              <input
                type="number"
                min="0.1"
                max="0.5"
                step="0.01"
                value={parameters.maximum_acceleration || 0.2}
                onChange={(e) => setParameters({...parameters, maximum_acceleration: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'VWAP' && (
        <div style={{ marginBottom: 20 }}>
          <h4>VWAP Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>Period</label>
              <input
                type="number"
                min="10"
                max="100"
                value={parameters.vwap_period || 20}
                onChange={(e) => setParameters({...parameters, vwap_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Deviation Threshold</label>
              <input
                type="number"
                min="0.005"
                max="0.1"
                step="0.001"
                value={parameters.deviation_threshold || 0.02}
                onChange={(e) => setParameters({...parameters, deviation_threshold: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Volume Threshold</label>
              <input
                type="number"
                min="1.0"
                max="5.0"
                step="0.1"
                value={parameters.volume_threshold || 1.5}
                onChange={(e) => setParameters({...parameters, volume_threshold: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'ATR' && (
        <div style={{ marginBottom: 20 }}>
          <h4>ATR Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>ATR Period</label>
              <input
                type="number"
                min="5"
                max="50"
                value={parameters.atr_period || 14}
                onChange={(e) => setParameters({...parameters, atr_period: parseInt(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>ATR Multiplier</label>
              <input
                type="number"
                min="1.0"
                max="5.0"
                step="0.1"
                value={parameters.atr_multiplier || 2.0}
                onChange={(e) => setParameters({...parameters, atr_multiplier: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Volatility Threshold</label>
              <input
                type="number"
                min="0.005"
                max="0.1"
                step="0.001"
                value={parameters.volatility_threshold || 0.02}
                onChange={(e) => setParameters({...parameters, volatility_threshold: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      {strategy === 'HYBRID' && (
        <div style={{ marginBottom: 20 }}>
          <h4>Hybrid Strategy Parameters</h4>
          <div style={{ display: 'grid', gap: 16 }}>
            <div className="form-group">
              <label>Primary Strategy</label>
              <select
                value={parameters.primary_strategy || 'MA_CROSS'}
                onChange={(e) => setParameters({...parameters, primary_strategy: e.target.value})}
                className="form-control"
              >
                <option value="MA_CROSS">Moving Average Crossover</option>
                <option value="RSI">RSI Mean Reversion</option>
                <option value="BOLLINGER">Bollinger Bands</option>
                <option value="MACD">MACD</option>
                <option value="STOCHASTIC">Stochastic Oscillator</option>
                <option value="ICHIMOKU">Ichimoku Cloud</option>
                <option value="PARABOLIC_SAR">Parabolic SAR</option>
                <option value="VWAP">VWAP</option>
                <option value="ATR">ATR</option>
              </select>
            </div>
            <div className="form-group">
              <label>Secondary Strategy</label>
              <select
                value={parameters.secondary_strategy || 'RSI'}
                onChange={(e) => setParameters({...parameters, secondary_strategy: e.target.value})}
                className="form-control"
              >
                <option value="MA_CROSS">Moving Average Crossover</option>
                <option value="RSI">RSI Mean Reversion</option>
                <option value="BOLLINGER">Bollinger Bands</option>
                <option value="MACD">MACD</option>
                <option value="STOCHASTIC">Stochastic Oscillator</option>
                <option value="ICHIMOKU">Ichimoku Cloud</option>
                <option value="PARABOLIC_SAR">Parabolic SAR</option>
                <option value="VWAP">VWAP</option>
                <option value="ATR">ATR</option>
              </select>
            </div>
            <div className="form-group">
              <label>Confirmation Threshold</label>
              <input
                type="number"
                min="0.1"
                max="1.0"
                step="0.1"
                value={parameters.confirmation_threshold || 0.5}
                onChange={(e) => setParameters({...parameters, confirmation_threshold: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Weight Primary</label>
              <input
                type="number"
                min="0.1"
                max="0.9"
                step="0.1"
                value={parameters.weight_primary || 0.7}
                onChange={(e) => setParameters({...parameters, weight_primary: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Weight Secondary</label>
              <input
                type="number"
                min="0.1"
                max="0.9"
                step="0.1"
                value={parameters.weight_secondary || 0.3}
                onChange={(e) => setParameters({...parameters, weight_secondary: parseFloat(e.target.value)})}
                className="form-control"
              />
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={loading}
        className="admin-button primary"
        style={{ padding: '12px 24px', fontSize: 16 }}
      >
        {loading ? 'Saving...' : 'Save Strategy Configuration'}
      </button>

      <div style={{ marginTop: 20, padding: 12, backgroundColor: '#e3f2fd', borderRadius: 4 }}>
        <h5>Current Configuration:</h5>
        <pre style={{ margin: 0, fontSize: 12 }}>
          {JSON.stringify({ strategy, parameters }, null, 2)}
        </pre>
        {!initialized && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Note: Loading existing configuration from Python service...
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleStrategyConfig;
